import * as React from 'react';
import { Button, Input, notification, Popconfirm, Radio, Select, Space, Table, Tag } from 'antd';
import { useNavigate, Outlet } from 'react-router-dom';
import { PageContainer } from '@ant-design/pro-layout';
import { PlusOutlined, DeleteOutlined, EyeOutlined, EditOutlined, SearchOutlined, AppstoreOutlined, UnorderedListOutlined } from '@ant-design/icons';
import { useAtom } from 'jotai';
import { useDeleteCampaign } from './queryHooks';
import { campaignsAtom, campaignSearchAtom, campaignStatusAtom, campaignBrandAtom, campaignViewModeAtom } from './atoms';
import type { Campaign } from './types';
import CampaignsBoard from './CampaignsBoard';

const statusColor = (s?: string) => {
    switch (s) {
        case 'spec':
            return 'gold';
        case 'active':
            return 'green';
        case 'completed':
            return 'blue';
        case 'canceled':
        case 'cancelled':
            return 'red';
        default:
            return 'default';
    }
};

const CampaignsList: React.FC = () => {
    const navigate = useNavigate();
    const [{ data: campaigns = [], isPending: isLoading }] = useAtom(campaignsAtom);
    const del = useDeleteCampaign();

    const [search, setSearch] = useAtom(campaignSearchAtom);
    const [status, setStatus] = useAtom(campaignStatusAtom);
    const [brand, setBrand] = useAtom(campaignBrandAtom);
    const [viewMode, setViewMode] = useAtom(campaignViewModeAtom);

    // Unique brand options derived from loaded campaigns.
    const brandOptions = React.useMemo(() => {
        const seen = new Map<number, string>();
        for (const c of campaigns) {
            if (c.brand?.id != null && c.brand?.name) seen.set(c.brand.id, c.brand.name);
        }
        return Array.from(seen.entries())
            .sort((a, b) => a[1].localeCompare(b[1]))
            .map(([id, name]) => ({ label: name, value: String(id) }));
    }, [campaigns]);

    const clearFilters = () => {
        setSearch(undefined);
        setStatus(undefined);
        setBrand(undefined);
    };
    const hasActiveFilters = !!(search || status || brand);

    return (
        <PageContainer
            title="Campaigns"
            extra={[
                <Radio.Group
                    key="view-mode"
                    value={viewMode}
                    onChange={(e) => setViewMode(e.target.value)}
                    optionType="button"
                    buttonStyle="solid"
                    size="middle"
                >
                    <Radio.Button value="table">
                        <UnorderedListOutlined /> Table
                    </Radio.Button>
                    <Radio.Button value="board">
                        <AppstoreOutlined /> Board
                    </Radio.Button>
                </Radio.Group>,
                <Button
                    key="new"
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={() => navigate('/creators/campaigns/new')}
                >
                    New Campaign
                </Button>
            ]}
        >
            <Space wrap style={{ marginBottom: 12 }}>
                <Input
                    allowClear
                    prefix={<SearchOutlined />}
                    placeholder="Search name or notes…"
                    value={search ?? ''}
                    onChange={(e) => setSearch(e.target.value || undefined)}
                    style={{ width: 260 }}
                />
                <Select
                    allowClear
                    placeholder="Status"
                    value={status}
                    onChange={(v) => setStatus(v)}
                    style={{ width: 160 }}
                    options={[
                        { label: 'Spec', value: 'spec' },
                        { label: 'Active', value: 'active' },
                        { label: 'Completed', value: 'completed' },
                        { label: 'Canceled', value: 'canceled' }
                    ]}
                />
                <Select
                    allowClear
                    showSearch
                    placeholder="Brand"
                    value={brand}
                    onChange={(v) => setBrand(v)}
                    style={{ width: 200 }}
                    options={brandOptions}
                    filterOption={(input, option) =>
                        String(option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                    }
                />
                {hasActiveFilters && (
                    <Button size="small" onClick={clearFilters}>
                        Clear filters
                    </Button>
                )}
            </Space>

            {viewMode === 'board' ? (
                <CampaignsBoard campaigns={campaigns} />
            ) : (
            <Table<Campaign>
                rowKey="id"
                loading={isLoading}
                dataSource={campaigns}
                pagination={{ pageSize: 20 }}
                columns={[
                    {
                        title: 'Name',
                        dataIndex: 'name',
                        render: (name: string, row) => (
                            <a onClick={() => navigate(`/creators/campaigns/${row.id}`)}>{name}</a>
                        )
                    },
                    {
                        title: 'Created on',
                        dataIndex: 'created_at',
                        render: (v: string) => (v ? new Date(v).toLocaleDateString() : '-')
                    },
                    {
                        title: 'Created by',
                        dataIndex: 'created_by',
                        render: (v: string) => v || <span style={{ color: 'var(--text-faint)' }}>—</span>
                    },
                    {
                        title: 'Brand',
                        dataIndex: 'brand',
                        render: (b: Campaign['brand']) =>
                            b?.name
                                ? <Tag color="geekblue" style={{ margin: 0 }}>{b.name}</Tag>
                                : <span style={{ color: 'var(--text-faint)' }}>—</span>
                    },
                    {
                        title: 'Budget (€)',
                        dataIndex: 'budget',
                        align: 'right',
                        render: (v: number | null) =>
                            v != null
                                ? new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v)
                                : <span style={{ color: 'var(--text-faint)' }}>—</span>
                    },
                    {
                        title: 'Creators',
                        dataIndex: 'creator_count',
                        align: 'center',
                        render: (n: number) => n ?? 0
                    },
                    {
                        title: 'Start Date',
                        dataIndex: 'start_date',
                        render: (v: string) => (v ? new Date(v).toLocaleDateString() : <span style={{ color: 'var(--text-faint)' }}>—</span>)
                    },
                    {
                        title: 'End Date',
                        dataIndex: 'end_date',
                        render: (v: string) => (v ? new Date(v).toLocaleDateString() : <span style={{ color: 'var(--text-faint)' }}>—</span>)
                    },
                    {
                        title: 'Actions',
                        key: 'actions',
                        width: 120,
                        render: (_, row) => (
                            <Space>
                                <Button
                                    size="small"
                                    icon={<EyeOutlined />}
                                    onClick={() => navigate(`/creators/campaigns/${row.id}`)}
                                />
                                <Button
                                    size="small"
                                    icon={<EditOutlined />}
                                    onClick={() => navigate(`/creators/campaigns/${row.id}/edit`)}
                                />
                                <Popconfirm
                                    title="Delete this campaign?"
                                    onConfirm={() =>
                                        del.mutate(row.id!, {
                                            onSuccess: () =>
                                                notification.success({ message: 'Deleted' }),
                                            onError: () =>
                                                notification.error({ message: 'Delete failed' })
                                        })
                                    }
                                >
                                    <Button size="small" danger icon={<DeleteOutlined />} />
                                </Popconfirm>
                            </Space>
                        )
                    }
                ]}
            />
            )}
            <Outlet />
        </PageContainer>
    );
};

export default CampaignsList;
